import BaseAPI from './BaseAPI';
import configuration from '../configuration';

/**
 * One node of a filter tree sent to analytics-service: either a group node (op + clauses) or a
 * leaf node (field + operator + values). The service validates field/operator against its own
 * whitelist (senderAccountId, recipientAccountId, hasAttachment; EQ, IN) — org scoping is never
 * expressible here, since the service always applies it itself.
 */
export type ArchivingFilterNode = {
  op?: 'AND' | 'OR';
  clauses?: ArchivingFilterNode[];
  field?: 'senderAccountId' | 'recipientAccountId' | 'hasAttachment';
  operator?: 'EQ' | 'IN';
  values?: string[];
};

export type ArchivingMessageSearchRequest = {
  orgId: string;
  query: string;
  startDate?: string;
  endDate?: string;
  hasAttachment?: boolean;
  senderAccountIds?: string[];
  recipientAccountIds?: string[];
  filter?: ArchivingFilterNode;
  limit?: number;
  columns?: string[];
};

/**
 * Known fields on a search result row — kept optional/loose beyond this since the exact backend
 * column set is still a placeholder pending data eng confirmation (see
 * analytics-service/docs/ARCHIVING_MESSAGE_SEARCH.md's "Known placeholders" notes). recipientAccountId
 * is null for a group/DL result (see analytics-service's DISTINCT dedupe); channelId is null for a
 * p2p result, populated for group/DL.
 */
export type ArchivingMessageSearchResult = {
  messageId?: string;
  sentAt?: string;
  senderDisplayName?: string;
  recipientAccountId?: string | null;
  body?: string;
  hasAttachment?: boolean;
  conversationId?: string;
  channelId?: string | null;
};

export type ArchivingMessageSearchData = {
  results: ArchivingMessageSearchResult[];
  resultCount: number;
};

export type ArchivingMessageSearchMetadata = {
  processingTimeMs?: number;
  snowflakeAccount?: string;
};

/** status/requestId/timestamp envelope shared by every Archiving response type. */
export type ArchivingResponseEnvelope = {
  status: string;
  requestId: string;
  timestamp: string;
};

export type ArchivingMessageSearchResponse = ArchivingResponseEnvelope & {
  data: ArchivingMessageSearchData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingConversationRequest = {
  orgId: string;
  conversationId: string;
  limit?: number;
};

/**
 * Known fields on a conversation message — same placeholder caveat as ArchivingMessageSearchResult.
 * No recipientAccountId here (unlike search): the conversation endpoint omits it entirely rather
 * than nulling it, since this view shows the thread rather than who received what.
 */
export type ArchivingConversationMessage = {
  messageId?: string;
  sentAt?: string;
  senderDisplayName?: string;
  body?: string;
  hasAttachment?: boolean;
  conversationId?: string;
  channelId?: string | null;
};

export type ArchivingConversationData = {
  messages: ArchivingConversationMessage[];
};

export type ArchivingConversationResponse = ArchivingResponseEnvelope & {
  data: ArchivingConversationData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingConversationMetadataRequest = {
  orgId: string;
  conversationId: string;
};

export type ArchivingConversationMetadataData = {
  conversationId: string;
  channelId: string | null;
  messageCount: number;
};

export type ArchivingConversationMetadataResponse = ArchivingResponseEnvelope & {
  data: ArchivingConversationMetadataData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingExportRequest = {
  orgId: string;
  exportName?: string;
  /**
   * Message identifiers to export. The caller resolves all product use cases (selected messages, a
   * full conversation thread, a saved search) into this one list before calling startExport, so the
   * export always reflects exactly what the user reviewed rather than a live re-query at export time.
   */
  messageIds: string[];
  includeAttachments?: boolean;
  notes?: string;
};

export type ArchivingExportData = {
  exportJobId: string;
  /** Job lifecycle status. Always 'queued' for now — no job processing exists yet. */
  jobStatus: string;
};

export type ArchivingExportResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportData;
};

export type ArchivingExportListRequest = {
  orgId: string;
};

export type ArchivingExportSummary = {
  exportJobId: string;
  exportName?: string;
  /** 'queued' | 'processing' | 'ready' | 'failed' */
  jobStatus: string;
  createdAt: string;
  /**
   * When this job's TTL semantics say it should be considered gone — recomputed on every status
   * transition server-side (queued/processing/ready/failed each have their own window). Check this
   * before treating a 'ready' job as downloadable; analytics-service's download endpoint enforces
   * it too, but the UI shouldn't rely solely on that round trip to decide what to show as available.
   */
  expiresAt: string;
};

export type ArchivingExportListData = {
  /** The caller's own export jobs, most recent first — never every export in the org (no archiving-admin role yet). */
  exports: ArchivingExportSummary[];
};

export type ArchivingExportListResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportListData;
};

export type ArchivingExportDownloadRequest = {
  orgId: string;
  jobId: string;
};

export type ArchivingExportDownloadData = {
  downloadUrl: string;
  /** When this presigned URL itself expires (15 minutes from issuance) — not the export job's own expiresAt. */
  urlExpiresAt: string;
};

export type ArchivingExportDownloadResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportDownloadData;
};

export default class ArchivingAPI extends BaseAPI {
  // analytics-service lives at api.{env}.tigerconnect.com, not the devapi base URL
  private get tcBaseUrl(): string {
    const { apiEnv } = this.config;
    return configuration.TC_URLS[apiEnv] || `https://api.${apiEnv}.tigerconnect.com`;
  }

  searchMessages = async (
    organizationId: string,
    payload: ArchivingMessageSearchRequest
  ): Promise<ArchivingMessageSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/search`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingMessageSearchResponse;
  };

  getConversation = async (
    organizationId: string,
    payload: ArchivingConversationRequest
  ): Promise<ArchivingConversationResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/conversation`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingConversationResponse;
  };

  getConversationMetadata = async (
    organizationId: string,
    payload: ArchivingConversationMetadataRequest
  ): Promise<ArchivingConversationMetadataResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/conversation/metadata`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingConversationMetadataResponse;
  };

  startExport = async (
    organizationId: string,
    payload: ArchivingExportRequest
  ): Promise<ArchivingExportResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/export`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingExportResponse;
  };

  listExports = async (
    organizationId: string,
    payload: ArchivingExportListRequest
  ): Promise<ArchivingExportListResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/export`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingExportListResponse;
  };

  getExportDownloadUrl = async (
    organizationId: string,
    payload: ArchivingExportDownloadRequest
  ): Promise<ArchivingExportDownloadResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/export/${payload.jobId}/download`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingExportDownloadResponse;
  };
}
